const managedPreview = ".item-preview-frame, .item-preview-viewport, .hotspot-viewport, .static-overlay-artwork, .interaction-preview-dialog";

// UI-only state. Never store offsets/disclosure choices in a playlist or carry
// a pending view restoration into another account, route or draft.
export function createEditorViewState() {
  let previousScope = null;
  const sections = new WeakMap();
  const same = (a, b) => !!a && !!b && a.length === b.length && a.every((value, index) => value === b[index]);
  const nodes = (host, selector) => Array.from(host.querySelectorAll?.(selector) ?? []);
  const locator = (host, node) => {
    if (node.dataset?.scrollKey) return { selector: `[data-scroll-key="${node.dataset.scrollKey}"]`, index: 0 };
    if (node.id && /^[a-zA-Z][\w-]*$/.test(node.id)) return { selector: `#${node.id}`, index: 0 };
    const classes = Array.from(node.classList ?? []).filter(value => /^[a-zA-Z][\w-]*$/.test(value));
    const selector = classes.length ? classes.map(value => `.${value}`).join("") : node.tagName?.toLowerCase();
    return selector ? { selector, index: nodes(host, selector).indexOf(node) } : null;
  };
  return {
    capture(host, scope) {
      if (!same(previousScope, scope)) return null;
      const positions = [];
      for (const node of nodes(host, "*")) {
        if (node.closest?.(managedPreview)) continue;
        if (!(node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1 || node.scrollLeft || node.scrollTop)) continue;
        const location = locator(host, node);
        if (location) positions.push({ ...location, node, left: node.scrollLeft, top: node.scrollTop });
      }
      const page = host.ownerDocument?.scrollingElement;
      return { scope, positions, page: page ? { node: page, left: page.scrollLeft, top: page.scrollTop } : null };
    },
    restore(host, snapshot, scope) {
      previousScope = [...scope];
      if (!snapshot || !same(snapshot.scope, scope)) return;
      // Run synchronously after the new DOM and disclosure states exist. No
      // delayed callback can undo a subsequent wheel, drag or keyboard gesture.
      for (const position of snapshot.positions) {
        const node = position.node.isConnected ? position.node : nodes(host, position.selector)[position.index];
        if (!node || node.closest?.(managedPreview)) continue;
        node.scrollLeft = Math.max(0, Math.min(position.left, node.scrollWidth - node.clientWidth));
        node.scrollTop = Math.max(0, Math.min(position.top, node.scrollHeight - node.clientHeight));
      }
      if (snapshot.page && snapshot.page.node === host.ownerDocument?.scrollingElement) {
        const { node, left, top } = snapshot.page;
        node.scrollLeft = Math.max(0, Math.min(left, node.scrollWidth - node.clientWidth));
        node.scrollTop = Math.max(0, Math.min(top, node.scrollHeight - node.clientHeight));
      }
    },
    bindSections(host, item, isCurrent) {
      if (!item || typeof item !== "object") return;
      let saved = sections.get(item);
      if (!saved) { saved = new Map(); sections.set(item, saved); }
      for (const node of nodes(host, "details[data-editor-section]")) {
        const key = node.dataset.editorSection;
        if (saved.has(key)) node.open = saved.get(key);
        else saved.set(key, node.open);
        node.addEventListener("toggle", () => {
          if (node.isConnected && isCurrent()) saved.set(key, node.open);
        });
      }
    },
    transferSections(previous, replacement) {
      const saved = sections.get(previous);
      if (saved) sections.set(replacement, new Map(saved));
    },
  };
}
